/**
 * Wrapper seguro sobre localStorage / sessionStorage.
 *
 * Cualquier acceso a localStorage/sessionStorage puede LANZAR: modo privado,
 * almacenamiento lleno, cuota excedida, o navegadores con protección estricta
 * (Firefox lanza NS_ERROR_FAILURE). Un throw sin capturar durante el arranque
 * (inicialización de un store Pinia, guard del router, hook de un layout) dejaba
 * la app en PANTALLA EN BLANCO.
 *
 * Este helper captura el error y degrada de forma segura (null / no-op) en vez
 * de tumbar el bootstrap. Es una práctica defensiva estándar: la app sigue
 * funcionando aunque el navegador niegue el almacenamiento (la sesión
 * simplemente no persistirá entre recargas en ese caso).
 */

function getStore(kind: 'local' | 'session'): Storage | null {
    try {
        return kind === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
        // Acceder al objeto Storage puede lanzar por sí solo con protección estricta.
        return null;
    }
}

export const safeStorage = {
    getItem(key: string, kind: 'local' | 'session' = 'local'): string | null {
        try {
            return getStore(kind)?.getItem(key) ?? null;
        } catch {
            return null;
        }
    },

    setItem(key: string, value: string, kind: 'local' | 'session' = 'local'): void {
        try {
            getStore(kind)?.setItem(key, value);
        } catch {
            /* almacenamiento no disponible: no-op */
        }
    },

    removeItem(key: string, kind: 'local' | 'session' = 'local'): void {
        try {
            getStore(kind)?.removeItem(key);
        } catch {
            /* almacenamiento no disponible: no-op */
        }
    },

    /**
     * getItem + JSON.parse tolerante. Devuelve `fallback` si el almacenamiento
     * no está disponible o el valor no es JSON válido.
     */
    getJSON<T>(key: string, fallback: T, kind: 'local' | 'session' = 'local'): T {
        const raw = safeStorage.getItem(key, kind);
        if (raw == null) return fallback;
        try {
            return JSON.parse(raw) as T;
        } catch {
            return fallback;
        }
    }
};
